import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Building2, Instagram } from 'lucide-react';
import Layout from '@/components/Layout';
import Container from '@/components/ui/Container';
import SectionHeading from '@/components/ui/SectionHeading';
import PageHero from '@/components/PageHero';
import ConsultationForm from '@/components/ConsultationForm';
import { WhatsAppIcon, WHATSAPP_URL } from '@/components/WhatsAppButton';
import { offices } from '@/data/offices';

function Contact() {
  return (
    <Layout>
      <PageHero
        eyebrow="Contato"
        title="Vamos falar sobre a segurança da sua empresa"
        description="Entre em contato pelos canais abaixo ou preencha o formulário para receber uma proposta personalizada de nossos consultores." />


      <section className="py-20 sm:py-24">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-5 lg:items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <SectionHeading
              eyebrow="Fale conosco"
              title="Canais diretos de atendimento" />

            <div className="mt-8 space-y-4">
              <a href="tel:+5521972265440" className="flex items-start gap-3 rounded-xl border border-slate-100 p-4 transition-colors hover:border-cyan-200">
                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Telefone</p>
                  <p className="text-sm text-slate-600">(21) 97226-5440</p>
                </div>
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 rounded-xl border border-slate-100 p-4 transition-colors hover:border-emerald-200">

                <WhatsAppIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#25D366]" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">WhatsApp</p>
                  <p className="text-sm text-slate-600">(21) 97226-5440 · iniciar conversa</p>
                </div>
              </a>
              <a href="mailto:comercial@frm.net.br" className="flex items-start gap-3 rounded-xl border border-slate-100 p-4 transition-colors hover:border-cyan-200">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">E-mail</p>
                  <p className="text-sm text-slate-600">comercial@frm.net.br</p>
                </div>
              </a>
              <a
                href="https://www.instagram.com/frmsolucoes"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 rounded-xl border border-slate-100 p-4 transition-colors hover:border-cyan-200">

                <Instagram className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Instagram</p>
                  <p className="text-sm text-slate-600">@frmsolucoes</p>
                </div>
              </a>
              <div className="flex items-start gap-3 rounded-xl border border-slate-100 p-4">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Endereço</p>
                  <p className="text-sm text-slate-600">Avenida Carlos Meziano, 482, Portuguesa, Rio de Janeiro, RJ</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-slate-100 p-4">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-cyan-600" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Horário de atendimento</p>
                  <p className="text-sm text-slate-600">Segunda a sexta, das 8h às 18h</p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Nossos escritórios</p>
              <div className="space-y-4">
                {offices.map((office) =>
                <div key={office.id} className="rounded-xl border border-slate-100 p-4">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-cyan-600" aria-hidden="true" />
                      <p className="text-sm font-semibold text-slate-900">{office.city}</p>
                      {office.isHq ?
                    <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-700">Matriz</span> :
                    null}
                    </div>
                    <p className="mt-2 flex items-start gap-2 text-xs text-slate-600">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
                      {office.address}
                    </p>
                    <p className="mt-1.5 text-xs text-slate-500">{office.phone} · {office.hours}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-100 p-6 shadow-sm sm:p-8">
              <SectionHeading
                eyebrow="Solicitação de consultoria"
                title="Solicite uma proposta personalizada"
                description="Preencha o formulário e receba uma proposta personalizada de nossos consultores em até 24 horas úteis." />

              <div className="mt-8">
                <ConsultationForm variant="full" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-slate-50 py-16 sm:py-20">
        <Container className="flex flex-col items-center gap-4 text-center">
          <SectionHeading
            title="Prefere se candidatar a uma vaga?"
            description="Conheça nossas oportunidades abertas e faça parte do nosso time."
            align="center" />

          <Link
            to="/carreiras"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-cyan-300 hover:text-cyan-700">

            Ver vagas abertas
          </Link>
        </Container>
      </section>
    </Layout>);

}

export default Contact;